import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Clock } from 'lucide-react';
import { ReportButton } from '@/components/ReportButton';
import NotificationBell from '@/components/NotificationBell';
import { toast } from 'sonner';
import { divinationLabel, DIVINATION_OPTIONS } from '@/data/divination';
import { reqNo } from '@/data/format';
import { birthDateText, birthTimeText } from '@/data/birth';

const RequestList = () => {
  const navigate = useNavigate();
  const { config, profileComplete } = useAuth();
  const maxPicks = config?.max_picks_per_request || 1;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [sortBy, setSortBy] = useState('match');

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/requests/available`, { withCredentials: true });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('無法載入求測單');
    } finally {
      setLoading(false);
    }
  };

  const regionOptions = useMemo(
    () => Array.from(new Set(requests.map((r) => r.region).filter(Boolean))),
    [requests]
  );

  const visible = useMemo(() => {
    let list = requests;
    if (typeFilter !== 'all') {
      list = list.filter((r) => {
        const types = r.divination_types || [];
        return types.includes(typeFilter) || types.includes('any');
      });
    }
    if (regionFilter !== 'all') {
      list = list.filter((r) => r.region === regionFilter);
    }
    return [...list].sort((a, b) => {
      // Already-answered requests always sink to the bottom.
      const aa = a.already_answered ? 1 : 0;
      const ba = b.already_answered ? 1 : 0;
      if (aa !== ba) return aa - ba;
      if (sortBy === 'match') {
        const am = a.matches_specialty ? 0 : 1;
        const bm = b.matches_specialty ? 0 : 1;
        if (am !== bm) return am - bm;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      const da = new Date(a.created_at).getTime();
      const db = new Date(b.created_at).getTime();
      return sortBy === 'newest' ? db - da : da - db;
    });
  }, [requests, typeFilter, regionFilter, sortBy]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4 flex justify-between items-center">
        <Link to="/astrologer/dashboard">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台</h2>
        </Link>
        <NotificationBell />
      </nav>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <Link to="/astrologer/dashboard">
          <Button variant="ghost" className="mb-6 rounded-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            返回
          </Button>
        </Link>

        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#1A1A1A] mb-2">
            公開求測單
          </h1>
          <p className="text-base text-[#5C5C5C] mb-4">
            選擇感興趣的求測單回答驗證問題
          </p>

          <div className="mb-6 bg-[#FBF7EF] border border-[#E8D9BF] rounded-sm p-4 text-sm leading-relaxed text-[#5C5C5C]" data-testid="incentive-strip">
            <span className="font-medium text-[#1A1A1A]">點解要回答？</span>
            盲答就係你嘅試場 — 答得具體、準確，求測者就會揀你開通諮詢；每張完結嘅求測單都會有評分，幫你累積「準度」往績。
          </div>

          {profileComplete === false && (
            <div className="mb-6 bg-[#FBF7EF] border border-[#E8D9BF] rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="browse-only-banner">
              <p className="text-sm text-[#1A1A1A]">
                你仲未建立個人頁 — 可以先隨便睇下有咩求測單，建立個人頁後就可以回答接案。
              </p>
              <Button
                onClick={() => navigate('/astrologer/dashboard')}
                className="bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shrink-0"
                data-testid="build-profile-btn"
              >
                建立個人頁
              </Button>
            </div>
          )}

          <div className="flex flex-wrap gap-3 mb-6 bg-white border border-[#E5E0D8] rounded-sm p-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-[#5C5C5C]">命理類型</span>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="rounded-sm w-[160px]" data-testid="filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部類型</SelectItem>
                  {DIVINATION_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-[#5C5C5C]">出生地區</span>
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="rounded-sm w-[160px]" data-testid="filter-region">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部地區</SelectItem>
                  {regionOptions.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-[#5C5C5C]">排序</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="rounded-sm w-[140px]" data-testid="filter-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="match">符合專長優先</SelectItem>
                  <SelectItem value="newest">最新優先</SelectItem>
                  <SelectItem value="oldest">最舊優先</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#5C5C5C]">載入中...</p>
            </div>
          ) : visible.length === 0 ? (
            <div className="bg-white border border-[#E5E0D8] rounded-sm p-12 text-center">
              <p className="text-sm text-[#5C5C5C]">
                {requests.length === 0 ? '目前沒有公開的求測單' : '沒有符合篩選條件的求測單'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {visible.map((req) => (
                <div
                  key={req.request_id}
                  className={`bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8 hover:shadow-md transition-shadow cursor-pointer ${req.already_answered ? 'opacity-70' : ''}`}
                  onClick={() => {
                    if (req.already_answered) {
                      navigate('/astrologer/answered');
                      return;
                    }
                    if (profileComplete === false) {
                      toast.info('建立個人頁後就可以回答求測單');
                      navigate('/astrologer/dashboard');
                      return;
                    }
                    navigate(`/astrologer/request/${req.request_id}`);
                  }}
                  data-testid={`request-${req.request_id}`}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="inline-flex items-center border border-[#C93B2B] text-[#C93B2B] px-2 py-1 text-xs rounded-none font-serif tracking-widest">
                          {divinationLabel(req)}
                        </div>
                        {req.seq && <span className="text-xs text-[#9C9C9C] font-mono">{reqNo(req.seq)}</span>}
                      </div>
                      <p className="text-sm text-[#5C5C5C] mt-2">求測者：{req.client_name}</p>
                    </div>
                    <div className="flex items-center text-sm text-[#5C5C5C]">
                      <Clock className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      {new Date(req.created_at).toLocaleDateString('zh-TW')}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {req.already_answered && (
                      <span className="inline-flex items-center bg-[#E6F4EA] text-[#1E7E34] px-2 py-0.5 text-xs rounded-sm" data-testid={`answered-badge-${req.request_id}`}>✓ 你已回答</span>
                    )}
                    {req.matches_specialty && (
                      <span className="inline-flex items-center bg-[#C93B2B] text-white px-2 py-0.5 text-xs rounded-sm">符合你的專長</span>
                    )}
                    {(req.answer_count ?? 0) === 0 ? (
                      <span className="inline-flex items-center bg-[#E6F4EA] text-[#1E7E34] px-2 py-0.5 text-xs rounded-sm">未有人回答 · 搶先答</span>
                    ) : (
                      <span className="inline-flex items-center bg-[#F5F0E6] text-[#5C5C5C] px-2 py-0.5 text-xs rounded-sm">{req.answer_count} 位已回答</span>
                    )}
                    {maxPicks > 1 && <span className="inline-flex items-center bg-[#F5F0E6] text-[#5C5C5C] px-2 py-0.5 text-xs rounded-sm">名額 {req.orders_count ?? 0}/{maxPicks}</span>}
                    <span className="inline-flex items-center bg-[#F5F0E6] text-[#5C5C5C] px-2 py-0.5 text-xs rounded-sm">仲剩 {req.days_left ?? 0} 天</span>
                    {(req.scopes || []).map((s) => (
                      <span key={`sc-${req.request_id}-${s}`} className="inline-flex items-center border border-[#E5E0D8] bg-white text-[#5C5C5C] px-2 py-0.5 text-xs rounded-sm">{s}</span>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-[#5C5C5C] mb-1">出生日期</p>
                      <p className="text-sm font-medium text-[#1A1A1A]">{birthDateText(req)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5C5C5C] mb-1">出生時間</p>
                      <p className="text-sm font-medium text-[#1A1A1A]">{birthTimeText(req)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5C5C5C] mb-1">性別</p>
                      <p className="text-sm font-medium text-[#1A1A1A]">{req.gender === 'male' ? '男' : '女'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#5C5C5C] mb-1">出生地區</p>
                      <p className="text-sm font-medium text-[#1A1A1A]">{req.region}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-[#C93B2B] font-bold mb-2">驗證問題 ({req.verification_questions.length})</p>
                    <div className="space-y-2">
                      {req.verification_questions.map((q) => (
                        <p key={`q-${q.order}`} className="text-sm text-[#5C5C5C]">
                          {q.order}. {q.question_text}
                        </p>
                      ))}
                    </div>
                  </div>

                  <Button
                    className="w-full mt-4 bg-[#C93B2B] text-white hover:bg-[#A82E20] rounded-sm shadow-sm"
                    data-testid={`answer-request-${req.request_id}`}
                  >
                    回答這個求測單
                  </Button>
                  <div className="mt-2 flex justify-end" onClick={(e) => e.stopPropagation()}>
                    <ReportButton
                      targetType="request"
                      targetId={req.request_id}
                      label="舉報此求測單"
                      testId={`report-request-${req.request_id}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestList;
